import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useShop } from '../context/ShopContext';

export default function ProductCard({ product }) {
  const { addToCart } = useShop();
  const [isHovered, setIsHovered] = useState(false);
  const [showSizes, setShowSizes] = useState(false);

  const isSoldOut = product.inventory === 0;

  return (
    <div 
      className="group flex flex-col h-full relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowSizes(false);
      }}
    >
      <div className="aspect-[3/4] overflow-hidden bg-bone border border-graphite/10 mb-6 relative">
        <img 
          src={product.image} 
          alt={product.name} 
          className={`w-full h-full object-cover transition-transform duration-[1.5s] ease-out ${isHovered ? 'scale-105' : 'scale-100'}`}
        />
        
        {isSoldOut && (
          <div className="absolute inset-0 bg-bone/80 flex items-center justify-center z-10">
            <span className="text-xs uppercase tracking-[0.2em] font-bold border-y border-graphite py-1">Sold Out</span>
          </div>
        )}

        {/* Desktop Quick Add Overlay */}
        {!isSoldOut && (
          <AnimatePresence>
            {isHovered && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-graphite/40 to-transparent flex flex-col justify-end gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
              >
                {!showSizes ? (
                  <button 
                    onClick={() => setShowSizes(true)}
                    className="bg-bone text-graphite py-3 text-[10px] uppercase tracking-widest font-bold hover:bg-crimson hover:text-bone transition-colors"
                  >
                    Quick Add +
                  </button>
                ) : (
                  <div className="grid grid-cols-3 gap-1">
                    {product.sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => {
                          addToCart(product, size);
                          setShowSizes(false);
                        }}
                        className="bg-bone/90 text-graphite py-2 text-[10px] font-bold hover:bg-crimson hover:text-bone transition-colors"
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
      
      <div className="flex-grow flex flex-col">
        <div className="flex justify-between items-start mb-3 gap-4">
          <h3 className="text-base font-medium tracking-tight leading-snug uppercase">{product.name}</h3>
          <span className="text-sm font-medium whitespace-nowrap">${product.price}</span>
        </div>
        <p className="text-xs text-graphite/50 leading-relaxed line-clamp-2 uppercase tracking-wide">{product.category}</p>
        
        {/* Mobile-only visible button */}
        {!isSoldOut && (
          <button 
            onClick={() => setShowSizes(!showSizes)}
            className="md:hidden mt-6 border border-graphite py-3 text-[10px] uppercase tracking-widest font-bold"
          >
            {showSizes ? 'Cancel' : 'Add to Bag'}
          </button>
        )}
        
        <AnimatePresence>
          {showSizes && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="md:hidden grid grid-cols-4 gap-2 mt-4"
            >
              {product.sizes.map(size => (
                <button
                  key={size}
                  onClick={() => addToCart(product, size)}
                  className="border border-graphite/20 py-2 text-[10px] font-bold hover:border-crimson hover:text-crimson"
                >
                  {size}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}